// ── GoalsManager — 매장 목표 관리 ──
// 4개 목표 (월매출/순이익률/원가율/인건비비율) 인라인 편집 + 달성률 게이지
import React, { useState, useEffect, useMemo } from 'react';
import {
  GOAL_DEFS, loadGoals, saveGoals,
  calcGoalActuals, calcAchievement, getAchievementColor, getAchievementLabel,
} from '../utils/goals';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function GoalsManager({ profitLoss = {}, monthKey, isMobile }) {
  const [goals, setGoals] = useState(loadGoals);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  // 자동 저장
  useEffect(() => { saveGoals(goals); }, [goals]);

  const record = profitLoss?.[monthKey] || {};
  const actuals = useMemo(() => calcGoalActuals(record, { monthKey }), [record, monthKey]);

  const startEdit = (goalDef) => {
    setEditingId(goalDef.id);
    setEditValue(String(goals[goalDef.id] || ''));
  };

  const saveEdit = () => {
    if (editingId !== null) {
      const num = parseFloat(editValue);
      if (!isNaN(num) && num > 0) {
        setGoals({ ...goals, [editingId]: num });
      }
    }
    setEditingId(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: isMobile ? '16px 14px' : '18px 22px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
    }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
          🎯 매장 목표 관리
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
          이번달 목표 vs 실적 — 클릭해서 목표값 수정
        </div>
        {(() => {
          const now = new Date();
          const todayMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          const isCurrent = monthKey === todayMonthKey;
          const todayDay = now.getDate();
          const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          if (!isCurrent || todayDay < 7) return null;
          return (
            <div style={{
              fontSize: 11, color: '#18181B', marginTop: 6,
              padding: '4px 8px', background: "#FAFAFA", borderRadius: 6,
              display: 'inline-block',
            }}>
              📈 월 매출 실적은 {todayDay}일까지 추세로 월말 ({daysInMonth}일) 예상 (run-rate)
            </div>
          );
        })()}
      </div>

      {/* 목표 행 — 게이지 바 제거, 숫자/배지 중심 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {GOAL_DEFS.map(def => {
          const target = goals[def.id];
          const actual = actuals[def.id];
          const pct = calcAchievement(def, target, actual);
          const color = getAchievementColor(pct);
          const isEditing = editingId === def.id;

          return (
            <div key={def.id} style={{
              padding: '12px 14px',
              background: '#fff',
              border: '1px solid #F4F4F5',
              borderRadius: 10,
            }}>
              {/* 1행: 라벨 + 목표 + 실적 + 달성률 */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                flexWrap: 'wrap',
              }}>
                {/* 라벨 */}
                <span style={{ fontSize: 13, fontWeight: 700, color: '#18181B',
                               minWidth: 100 }}>
                  {def.icon} {def.label}
                </span>

                {/* 목표값 (편집 가능) */}
                {isEditing ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      style={{
                        width: 100, padding: '4px 8px',
                        border: '1px solid #18181B',
                        borderRadius: 6, fontSize: 12, textAlign: 'right',
                        outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: 11, color: '#71717A' }}>
                      {def.unit === 'percent' ? '%' : '원'}
                    </span>
                    <button onClick={saveEdit} style={{
                      padding: '4px 10px', border: 'none',
                      background: '#18181B', color: '#fff',
                      borderRadius: 6, fontSize: 11, fontWeight: 700,
                      cursor: 'pointer',
                    }}>저장</button>
                    <button onClick={cancelEdit} style={{
                      padding: '4px 10px',
                      border: '1px solid #E4E4E7', background: '#fff',
                      color: '#71717A', borderRadius: 6, fontSize: 11,
                      cursor: 'pointer',
                    }}>취소</button>
                  </span>
                ) : (
                  <button onClick={() => startEdit(def)} title="클릭해서 목표 수정" style={{
                    padding: '4px 10px', borderRadius: 14,
                    border: '1px dashed #A1A1AA',
                    background: '#fff', color: '#52525B',
                    fontSize: 12, fontWeight: 700,
                    cursor: 'pointer',
                  }}>
                    목표 {def.unit === 'percent' ? `${target}%` : `${fmt(target)}원`}
                  </button>
                )}

                {/* 화살표 → */}
                <span style={{ color: '#A1A1AA', fontSize: 12 }}>→</span>

                {/* 실적 */}
                <span style={{ fontSize: 14, fontWeight: 800, color: '#18181B' }}>
                  {actual === null ? '—'
                    : def.unit === 'percent' ? `${actual.toFixed(1)}%`
                    : `${fmt(actual)}원`}
                </span>

                {/* 달성률 배지 */}
                <span style={{
                  marginLeft: 'auto',
                  padding: '4px 12px', borderRadius: 12,
                  background: pct === null ? '#F4F4F5' : color,
                  color: pct === null ? '#A1A1AA' : '#fff',
                  fontSize: 11, fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}>
                  {pct === null ? '—' : `${pct.toFixed(0)}%`} {pct !== null && `· ${getAchievementLabel(pct)}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 도움말 */}
      <div style={{
        marginTop: 12, padding: '8px 12px',
        background: '#FAFAFA', borderRadius: 8,
        fontSize: 11, color: '#52525B', lineHeight: 1.6,
      }}>
        식재료 원가율 · 인건비 비율은 <strong>낮을수록 좋아요</strong>. 100% 달성 시 배지에 표시됩니다.
      </div>
    </div>
  );
}
